import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import {
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
  FilterKeyword,
  FilterKeywordField,
  EuqTimeRangeEnum,
  ServiceTypeFilter,
} from '../../type';
import { FILTER_DEBOUNCE_DELAY } from '../constants';

interface UseFiltersProps {
  initialStatus?: FeedbackFilterStatus;
  initialCategory?: FeedbackFilterCategory;
  initialResultCategory?: FeedbackResultCategory;
  timeRange: EuqTimeRangeEnum;
}

// Storage key for CECP EUQ filters
const STORAGE_KEY = 'cecp_euq_filters';

export type RequestSourceFilter =
  | 'all'
  | 'Plugin'
  | 'Banner'
  | 'AutoCollect'
  | 'CentralOperation';

// Type for stored filter state
interface StoredFilterState {
  status: FeedbackFilterStatus;
  category: FeedbackFilterCategory;
  resultCategory: FeedbackResultCategory;
  keyword: FilterKeyword;
  sortKey: string;
  securityActions: string;
  serviceType: ServiceTypeFilter;
  requestSource: RequestSourceFilter;
}

// Helper function to check if a value is a valid enum value
const isValidEnumValue = <T extends Record<string, string>>(
  value: any,
  enumObject: T
): value is T[keyof T] => {
  return Object.values(enumObject).includes(value);
};

// Helper functions for localStorage management
const getFiltersFromStorage = (): Partial<StoredFilterState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to parse stored filters:', error);
    return null;
  }
};

const saveFiltersToStorage = (filters: Partial<StoredFilterState>) => {
  try {
    const stored = getFiltersFromStorage() || {};
    const updated = { ...stored, ...filters };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to save filters to storage:', error);
  }
};

const clearFiltersFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear filters from storage:', error);
  }
};

export const useFilters = ({
  initialStatus = FeedbackFilterStatus.All,
  initialCategory = FeedbackFilterCategory.All,
  initialResultCategory = FeedbackResultCategory.All,
  timeRange,
}: UseFiltersProps) => {
  const isInitializedRef = useRef(false);

  // Get values from localStorage with fallbacks
  const getInitialState = useCallback(() => {
    const stored = getFiltersFromStorage();

    if (!stored) {
      return {
        status: initialStatus,
        category: initialCategory,
        resultCategory: initialResultCategory,
        keyword: {
          recipient: '',
          sender: '',
          reporter: '',
          subject: '',
        },
        sortKey: '-created_at',
        securityActions: 'all',
        serviceType: ServiceTypeFilter.All,
        requestSource: 'all' as RequestSourceFilter,
      };
    }

    // Validate enum values from storage
    const validStatus = isValidEnumValue(stored.status, FeedbackFilterStatus)
      ? stored.status
      : initialStatus;
    const validCategory = isValidEnumValue(
      stored.category,
      FeedbackFilterCategory
    )
      ? stored.category
      : initialCategory;
    const validResultCategory = isValidEnumValue(
      stored.resultCategory,
      FeedbackResultCategory
    )
      ? stored.resultCategory
      : initialResultCategory;
    const validServiceType = isValidEnumValue(
      stored.serviceType,
      ServiceTypeFilter
    )
      ? stored.serviceType
      : ServiceTypeFilter.All;

    const validRequestSource: RequestSourceFilter =
      stored.requestSource === 'AdminReport'
        ? 'CentralOperation'
        : (
              [
                'all',
                'Plugin',
                'Banner',
                'AutoCollect',
                'CentralOperation',
              ] as const
            ).includes(stored.requestSource as RequestSourceFilter)
          ? (stored.requestSource as RequestSourceFilter)
          : 'all';

    return {
      status: validStatus,
      category: validCategory,
      resultCategory: validResultCategory,
      keyword: stored.keyword || {
        recipient: '',
        sender: '',
        reporter: '',
        subject: '',
      },
      sortKey: stored.sortKey || '-created_at',
      securityActions: stored.securityActions || 'all',
      serviceType: validServiceType,
      requestSource: validRequestSource,
    };
  }, [initialStatus, initialCategory, initialResultCategory]);

  const initialState = getInitialState();

  const [status, setStatus] = useState<FeedbackFilterStatus>(
    initialState.status
  );
  const [category, setCategory] = useState<FeedbackFilterCategory>(
    initialState.category
  );
  const [resultCategory, setResultCategory] = useState<FeedbackResultCategory>(
    initialState.resultCategory
  );
  const [keyword, setKeyword] = useState<FilterKeyword>(initialState.keyword);
  const [sortKey, setSortKey] = useState<string>(initialState.sortKey);
  const [securityActions, setSecurityActions] = useState<string>(
    initialState.securityActions
  );
  const [serviceType, setServiceTypeState] = useState<ServiceTypeFilter>(
    initialState.serviceType
  );
  const [requestSource, setRequestSource] = useState<RequestSourceFilter>(
    initialState.requestSource
  );

  // Initialize state from localStorage only once on mount
  useEffect(() => {
    if (!isInitializedRef.current) {
      const initialState = getInitialState();
      setStatus(initialState.status);
      setCategory(initialState.category);
      setResultCategory(initialState.resultCategory);
      setKeyword(initialState.keyword);
      setSortKey(initialState.sortKey);
      setSecurityActions(initialState.securityActions);
      setServiceTypeState(initialState.serviceType);
      setRequestSource(initialState.requestSource);
      isInitializedRef.current = true;
    }
  }, [getInitialState]);

  // Update localStorage when filters change
  const updateStorage = useCallback((updates: Partial<StoredFilterState>) => {
    saveFiltersToStorage(updates);
  }, []);

  // Debounced storage update for subject field to prevent excessive writes
  const debouncedUpdateStorage = useMemo(
    () =>
      debounce((updates: Partial<StoredFilterState>) => {
        saveFiltersToStorage(updates);
      }, FILTER_DEBOUNCE_DELAY),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateStorage.cancel();
    };
  }, [debouncedUpdateStorage]);

  // Update state and localStorage together
  const updateStatus = useCallback(
    (newStatus: FeedbackFilterStatus) => {
      setStatus(newStatus);
      updateStorage({ status: newStatus });
    },
    [updateStorage]
  );

  const updateCategory = useCallback(
    (newCategory: FeedbackFilterCategory) => {
      setCategory(newCategory);
      updateStorage({ category: newCategory });
    },
    [updateStorage]
  );

  const updateResultCategory = useCallback(
    (newResultCategory: FeedbackResultCategory) => {
      setResultCategory(newResultCategory);
      updateStorage({ resultCategory: newResultCategory });
    },
    [updateStorage]
  );

  const updateSortKey = useCallback(
    (newSortKey: string) => {
      setSortKey(newSortKey);
      updateStorage({ sortKey: newSortKey });
    },
    [updateStorage]
  );

  const updateKeyword = useCallback(
    (field: FilterKeywordField, value: string) => {
      setKeyword((prev) => {
        const updated = { ...prev, [field]: value };

        // Use debounced storage update for subject field to prevent excessive writes
        if (field === 'subject') {
          debouncedUpdateStorage({ keyword: updated });
        } else {
          updateStorage({ keyword: updated });
        }

        return updated;
      });
    },
    [updateStorage, debouncedUpdateStorage]
  );

  const updateKeywordSubject = useCallback(
    (value: string) => {
      updateKeyword('subject', value);
    },
    [updateKeyword]
  );

  const updateSecurityActions = useCallback(
    (newSecurityActions: string) => {
      setSecurityActions(newSecurityActions);
      updateStorage({ securityActions: newSecurityActions });
    },
    [updateStorage]
  );

  const updateServiceType = useCallback(
    (newServiceType: ServiceTypeFilter) => {
      setServiceTypeState(newServiceType);
      updateStorage({ serviceType: newServiceType });
    },
    [updateStorage]
  );

  const updateRequestSource = useCallback(
    (next: RequestSourceFilter) => {
      setRequestSource(next);
      updateStorage({ requestSource: next });
    },
    [updateStorage]
  );

  const clearMainFilters = useCallback(() => {
    // Update state
    setStatus(FeedbackFilterStatus.All);
    setCategory(FeedbackFilterCategory.All);
    setResultCategory(FeedbackResultCategory.All);
    setSecurityActions('all');
    setServiceTypeState(ServiceTypeFilter.All);
    setRequestSource('all');

    // Update storage
    updateStorage({
      status: FeedbackFilterStatus.All,
      category: FeedbackFilterCategory.All,
      resultCategory: FeedbackResultCategory.All,
      securityActions: 'all',
      serviceType: ServiceTypeFilter.All,
      requestSource: 'all',
    });
  }, [updateStorage]);

  const resetFilters = useCallback(() => {
    setStatus(initialStatus);
    setCategory(FeedbackFilterCategory.All);
    setResultCategory(FeedbackResultCategory.All);
    setKeyword({
      recipient: '',
      sender: '',
      reporter: '',
      subject: '',
    });
    setSortKey('-created_at');
    setSecurityActions('all');
    setServiceTypeState(ServiceTypeFilter.All);
    setRequestSource('all');

    // Clear localStorage
    clearFiltersFromStorage();
  }, [initialStatus]);

  return {
    // State
    status,
    category,
    resultCategory,
    keyword,
    sortKey,
    timeRange,
    securityActions,
    serviceType,
    requestSource,

    // Actions
    setStatus: updateStatus,
    setCategory: updateCategory,
    setResultCategory: updateResultCategory,
    setSortKey: updateSortKey,
    setSecurityActions: updateSecurityActions,
    setServiceType: updateServiceType,
    setRequestSource: updateRequestSource,
    updateKeyword,
    updateKeywordSubject,
    resetFilters,
    clearMainFilters,
  };
};
